import { useCallback, useEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "../redux"
import { AppActions } from "../redux/appSlice"
import { getStripeProducts } from "../services"
import { useSubscription } from "./useSubscription"

export function useStripeProducts() {
  const dispatch = useAppDispatch()
  const isMountedRef = useRef(true)
  const hasFetchedRef = useRef(false)

  const userId = useAppSelector((state) => state.app.user?.uid)
  const isAuthenticated = useAppSelector((state) => !!state.app.user)
  const cachedProducts = useAppSelector((state) => state.app.stripeProducts)
  const isLoading = useAppSelector((state) => state.app.stripeProductsLoading)
  const error = useAppSelector((state) => state.app.stripeProductsError)

  const fetchProducts = useCallback(
    async (force = false) => {
      if (!isAuthenticated || !userId) {
        dispatch(AppActions.clearStripeProducts())
        return
      }

      const hasCache = cachedProducts && Object.keys(cachedProducts).length > 0
      if (!force && hasCache) return
      if (isLoading) return

      try {
        dispatch(AppActions.setStripeProductsLoading(true))
        dispatch(AppActions.setStripeProductsError(null))

        const products = await getStripeProducts()

        if (isMountedRef.current) {
          dispatch(
            AppActions.setStripeProducts({ products, lastFetch: Date.now() }),
          )
        }
      } catch (error: any) {
        if (isMountedRef.current) {
          const errorMessage =
            error?.response?.detail ||
            error?.message ||
            "Failed to fetch products"
          dispatch(AppActions.setStripeProductsError(errorMessage))
        }
      } finally {
        if (isMountedRef.current) {
          dispatch(AppActions.setStripeProductsLoading(false))
        }
      }
    },
    [userId, isAuthenticated, cachedProducts, isLoading, dispatch],
  )

  useEffect(() => {
    isMountedRef.current = true

    const hasCache = cachedProducts && Object.keys(cachedProducts).length > 0

    if (isAuthenticated && userId && !hasFetchedRef.current && !hasCache) {
      hasFetchedRef.current = true
      fetchProducts()
    } else if (!isAuthenticated || !userId) {
      hasFetchedRef.current = false
      dispatch(AppActions.clearStripeProducts())
    }

    return () => {
      isMountedRef.current = false
    }
  }, [userId, isAuthenticated, dispatch, cachedProducts, fetchProducts])

  useSubscription(cachedProducts || {})

  return {
    stripeProducts: cachedProducts || {},
    loading: isLoading,
    error,
    refetch: () => {
      hasFetchedRef.current = false
      return fetchProducts(true)
    },
  }
}
